import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Award, Download, Loader2, Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import api, { API } from "@/lib/api";

export default function AdminDevCertificatePage() {
  const [eligibility, setEligibility] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [eligRes, certRes] = await Promise.all([
        api.get("/admin-dev/certificate/eligibility"),
        api.get("/admin-dev/certificate/me"),
      ]);
      setEligibility(eligRes.data);
      setCertificate(certRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin-dev/certificate/generate");
      setCertificate(data);
      toast.success("Certificate generated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to generate certificate yet");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-dev-certificate-loading">
        <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-dev-certificate-page">
      <Link to="/admin-dev" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Admin + Dev Roadmap
      </Link>

      <div className="glass-panel rounded-2xl p-8 text-center" data-testid="admin-dev-certificate-panel">
        <Award className="w-14 h-14 text-[#FBBF24] mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-semibold text-white">Salesforce Admin + Dev 30-Day Certificate</h1>

        {certificate ? (
          <div className="mt-6" data-testid="admin-dev-certificate-ready">
            <p className="text-sm text-emerald-400 font-semibold flex items-center justify-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Certificate Ready</p>
            <p className="text-xs text-slate-400 mt-2">ID: {certificate.certificate_id}</p>
            <p className="text-xs text-slate-400">Score: {certificate.score}%</p>
            <a
              href={`${API}/admin-dev/certificate/${certificate.certificate_id}/pdf`} data-testid="admin-dev-certificate-download-button"
              className="inline-flex items-center gap-2 mt-5 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" /> Download Certificate PDF
            </a>
            <p className="text-[11px] text-slate-500 mt-3">Verify at: {certificate.verification_url}</p>
          </div>
        ) : eligibility?.eligible ? (
          <div className="mt-6" data-testid="admin-dev-certificate-eligible">
            <p className="text-sm text-slate-300">You've completed all {eligibility.total_days} days with an average score of {eligibility.avg_score}%. Generate your certificate now!</p>
            <button
              onClick={generate} disabled={generating} data-testid="admin-dev-certificate-generate-button"
              className="inline-flex items-center gap-2 mt-5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />} Generate Certificate
            </button>
          </div>
        ) : (
          <div className="mt-6" data-testid="admin-dev-certificate-locked">
            <Lock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-400">
              Complete all {eligibility?.total_days ?? 30} days (currently {eligibility?.completed_count ?? 0}) with an average quiz score of at least {eligibility?.passing_score ?? 80}% to unlock your certificate.
            </p>
            <Link to="/admin-dev" className="inline-block mt-4 text-amber-400 hover:underline text-sm">Continue the Challenge →</Link>
          </div>
        )}
      </div>
    </div>
  );
}
